<template>
  <div class="notif-bell-wrap">
    <button class="notif-bell-btn" :aria-label="t('notif_bellAria')" @click="open = !open">
      <SvgIcon name="bell" :size="18" />
      <span v-if="unreadCount > 0" class="notif-badge">{{
        unreadCount > 99 ? '99+' : unreadCount
      }}</span>
    </button>

    <div v-if="open" class="notif-panel" @click.stop>
      <div class="notif-header">
        <span class="notif-title">{{ t('notif_title') }}</span>
        <button v-if="unreadCount > 0" class="notif-mark-all" @click="markAllRead">
          {{ t('notif_markAllRead') }}
        </button>
      </div>
      <div v-if="all.length === 0" class="notif-empty">
        {{ t('notif_empty') }}
      </div>
      <ul v-else class="notif-list">
        <li
          v-for="n in all"
          :key="n.id"
          :class="[
            'notif-row',
            { 'notif-row--unread': !n.read, 'notif-row--warning': n.type === 'warning' },
          ]"
        >
          <button type="button" class="notif-item" @click="onItemClick(n)">
            <div class="notif-item-content">
              <div class="notif-item-title">
                {{ n.title }}
              </div>
              <div class="notif-item-body">
                {{ n.body }}
              </div>
            </div>
          </button>
          <button class="notif-dismiss" :aria-label="t('notif_dismiss')" @click="dismiss(n.id)">
            <SvgIcon name="delete" :size="12" />
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import SvgIcon from '@/ui/components/SvgIcon.vue';
import { useNotifications } from '@/core/useNotifications.js';

const { t } = useI18n();
const router = useRouter();
const { all, dismiss, markRead, unreadCount } = useNotifications();

const open = ref(false);

function onItemClick(n: { [key: string]: unknown; action_tab?: string; id: string }) {
  markRead(n.id);
  if (n.action_tab) {
    open.value = false;
    router.push(n.action_tab);
  }
}

function markAllRead() {
  for (const n of all.value) {
    if (!n.read) markRead(n.id);
  }
}

function onClickOutside(e: MouseEvent) {
  const target = e.target as HTMLElement;
  if (!target.closest('.notif-bell-wrap')) {
    open.value = false;
  }
}

onMounted(() => document.addEventListener('click', onClickOutside));
onUnmounted(() => document.removeEventListener('click', onClickOutside));
</script>

<style scoped>
.notif-bell-wrap {
  position: relative;
}

.notif-bell-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 0;
  border: 1px solid var(--border);
  background: var(--surface-raised);
  color: var(--muted);
  cursor: pointer;
  position: relative;
}
.notif-bell-btn:hover {
  color: var(--text);
}

.notif-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 0;
  background: var(--clr-danger);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
}

.notif-panel {
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 100;
  width: 320px;
  max-height: 400px;
  overflow-y: auto;
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.notif-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid var(--border);
}
.notif-title {
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--text);
}
.notif-mark-all {
  font-size: var(--text-xs);
  color: var(--clr-accent);
  background: none;
  border: none;
  cursor: pointer;
  font-weight: 600;
}

.notif-empty {
  padding: 20px 12px;
  text-align: center;
  font-size: var(--text-sm);
  color: var(--muted);
}

.notif-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

/* Row: the <li>, lays out the item button + dismiss button side by side and carries the
   hover/unread/warning background -- kept off .notif-item itself so that background shows
   through the item button rather than being clipped to just its own box. */
.notif-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--border);
  transition: background 0.1s;
}
.notif-row:hover {
  background: var(--surface-raised);
}
.notif-row--unread {
  background: rgba(var(--clr-accent-rgb, 0, 98, 204), 0.05);
}
.notif-row--unread .notif-item-title {
  font-weight: 700;
}

/* Item: a real <button> (not the row itself) so it can't contain the dismiss <button> as a
   descendant -- nesting interactive elements is invalid HTML and breaks click/keyboard handling. */
.notif-item {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: none;
  border: none;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.notif-item-content {
  flex: 1;
  min-width: 0;
}
.notif-item-title {
  font-size: var(--text-sm);
  color: var(--text);
}
.notif-item-body {
  font-size: var(--text-xs);
  color: var(--muted);
  margin-top: 2px;
  line-height: 1.3;
}

.notif-dismiss {
  flex-shrink: 0;
  background: none;
  border: none;
  color: var(--muted);
  cursor: pointer;
  padding: 2px;
  margin-top: 2px;
}
.notif-dismiss:hover {
  color: var(--clr-danger);
}
</style>
